import React, { useState, useEffect, useRef } from 'react';
import { Head } from '@inertiajs/react';
import { Building2, Bed, Trees, Mouse, ArrowRight } from 'lucide-react';
import FloatingMenu from '../../Components/FloatingMenu';
import MenuOverlay from '../../Components/MenuOverlay';
import Footer from '../../Components/Footer';
import SmoothScroll from '../../Components/SmoothScroll';

const VirtualTourPage = () => {
    const scrollContainerRef = useRef(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeFloor, setActiveFloor] = useState(1);

    useEffect(() => {
        const wrapper = scrollContainerRef.current;
        if (!wrapper) return;

        const handleScroll = (e) => {
            if (e.target.scrollTop > 50) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        wrapper.addEventListener('scroll', handleScroll);
        return () => wrapper.removeEventListener('scroll', handleScroll);
    }, []);

    const isBrandBoxVisible = !isScrolled || isMenuOpen;

    return (
        <SmoothScroll ref={scrollContainerRef}>
            <Head>
                <title>Virtual Tour - 360° View of Hollywood Royale Garden Assisted Living</title>
                <meta name="description" content="Take a 360° virtual tour of Hollywood Royale Garden assisted living facility. Explore our private and shared rooms, beautiful patios, common areas, and amenities in Hollywood, Los Angeles." />
                <meta name="keywords" content="virtual tour hollywood royale garden, 360 tour assisted living, senior living virtual tour los angeles, hollywood assisted living tour, virtual walkthrough senior care, online tour retirement home hollywood" />

                {/* Favicon */}
                <link rel="icon" type="image/png" href="/fevicon.png" />

                {/* Canonical URL */}
                <link rel="canonical" href="https://www.hrgalla.com/virtual-tour" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.hrgalla.com/virtual-tour" />
                <meta property="og:title" content="360° Virtual Tour - Hollywood Royale Garden Assisted Living" />
                <meta property="og:description" content="Experience Hollywood Royale Garden from anywhere. Take our interactive 360° virtual tour to explore our beautiful assisted living facility, private rooms, outdoor spaces, and premium amenities." />
                <meta property="og:image" content="https://www.hrgalla.com/hrlogo2.png" />
                <meta property="og:site_name" content="Hollywood Royale Garden" />
                <meta property="og:locale" content="en_US" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content="https://www.hrgalla.com/virtual-tour" />
                <meta name="twitter:title" content="360° Virtual Tour - Hollywood Royale Garden Assisted Living" />
                <meta name="twitter:description" content="Explore our luxury assisted living facility with our immersive 360° virtual tour. See our rooms, amenities, and beautiful spaces in Hollywood, CA." />
                <meta name="twitter:image" content="https://www.hrgalla.com/hrlogo2.png" />

                {/* Additional Meta Tags */}
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
                <meta name="language" content="English" />
                <meta name="author" content="Hollywood Royale Garden" />

                {/* Geo Tags */}
                <meta name="geo.region" content="US-CA" />
                <meta name="geo.placename" content="Los Angeles" />
                <meta name="geo.position" content="34.105459;-118.322046" />
                <meta name="ICBM" content="34.105459, -118.322046" />

                {/* Structured Data - Virtual Tour Schema */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "VirtualLocation",
                        "@id": "https://www.hrgalla.com/virtual-tour",
                        "name": "Hollywood Royale Garden Virtual Tour",
                        "description": "360° virtual tour of Hollywood Royale Garden assisted living facility in Hollywood, Los Angeles",
                        "url": "https://www.hrgalla.com/virtual-tour",
                        "image": "https://www.hrgalla.com/hrlogo2.png",
                        "provider": {
                            "@type": "AssistedLivingFacility",
                            "name": "Hollywood Royale Garden",
                            "image": "https://www.hrgalla.com/hrlogo2.png",
                            "telephone": "+1-323-466-2411",
                            "email": "info@hrgalla.com",
                            "address": {
                                "@type": "PostalAddress",
                                "streetAddress": "6054 Franklin Ave",
                                "addressLocality": "Los Angeles",
                                "addressRegion": "CA",
                                "postalCode": "90028",
                                "addressCountry": "US"
                            },
                            "geo": {
                                "@type": "GeoCoordinates",
                                "latitude": 34.105459,
                                "longitude": -118.322046
                            },
                            "url": "https://www.hrgalla.com",
                            "priceRange": "$$$$",
                            "amenityFeature": [
                                {
                                    "@type": "LocationFeatureSpecification",
                                    "name": "24/7 Professional Care",
                                    "value": true
                                },
                                {
                                    "@type": "LocationFeatureSpecification",
                                    "name": "Private and Shared Rooms",
                                    "value": true
                                },
                                {
                                    "@type": "LocationFeatureSpecification",
                                    "name": "Beautiful Outdoor Spaces",
                                    "value": true
                                },
                                {
                                    "@type": "LocationFeatureSpecification",
                                    "name": "Medication Management",
                                    "value": true
                                },
                                {
                                    "@type": "LocationFeatureSpecification",
                                    "name": "Housekeeping Services",
                                    "value": true
                                }
                            ]
                        }
                    })}
                </script>

                {/* Breadcrumb Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            {
                                "@type": "ListItem",
                                "position": 1,
                                "name": "Home",
                                "item": "https://www.hrgalla.com"
                            },
                            {
                                "@type": "ListItem",
                                "position": 2,
                                "name": "Virtual Tour",
                                "item": "https://www.hrgalla.com/virtual-tour"
                            }
                        ]
                    })}
                </script>
            </Head>

            <FloatingMenu
                isVisible={true}
                isScrolled={isScrolled}
                toggleMenu={() => setIsMenuOpen(!isMenuOpen)}
                isMenuOpen={isMenuOpen}
            />

            <MenuOverlay isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />

            <div className={`brand-box2 ${isBrandBoxVisible ? 'visible' : 'hidden'}`} onClick={() => window.location.href = '/'}>
                <img src="/hrlogo2.png" alt="Hollywood Royale" className="brand-image" />
            </div>

            <div style={{ height: '100px' }}></div>

            <main>
                {/* Hero Section */}
                <section style={{
                    padding: '80px 20px 60px',
                    background: 'linear-gradient(135deg, #f0f9f4 0%, #e8f5e9 50%, #f1f8f4 100%)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Decorative background elements */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at 20% 30%, rgba(212, 165, 117, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(10, 63, 38, 0.05) 0%, transparent 50%)',
                        pointerEvents: 'none'
                    }}></div>

                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        textAlign: 'center',
                        position: 'relative',
                        zIndex: 1
                    }}>
                        {/* Badge */}
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: 'rgba(212, 165, 117, 0.15)',
                            color: '#0a3f26',
                            padding: '10px 24px',
                            borderRadius: '50px',
                            fontSize: '14px',
                            fontWeight: '600',
                            marginBottom: '24px',
                            border: '1px solid rgba(212, 165, 117, 0.3)'
                        }}>
                            <Building2 size={18} strokeWidth={2.5} />
                            360° Virtual Experience
                        </div>

                        {/* Title */}
                        <h1 style={{
                            fontSize: 'clamp(36px, 6vw, 66px)',
                            fontWeight: '800',
                            marginBottom: '24px',
                            color: '#0a3f26',
                            fontFamily: "'Playfair Display', serif",
                            letterSpacing: '-0.5px',
                            lineHeight: '1.1'
                        }}>
                            Experience Hollywood Royale Garden
                        </h1>

                        {/* Subtitle */}
                        <p style={{
                            fontSize: 'clamp(18px, 2.5vw, 22px)',
                            lineHeight: '1.7',
                            color: '#2c5832',
                            maxWidth: '800px',
                            margin: '0 auto 20px',
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: '400'
                        }}>
                            Step inside and explore our luxury assisted living facility with our immersive 360° virtual tour.
                            Discover our beautiful spaces, premium amenities, and the warm atmosphere that makes Hollywood Royale Garden feel like home.
                        </p>

                        {/* Instructions */}
                        <div style={{
                            fontSize: '15px',
                            color: '#d4a575',
                            fontFamily: "'Inter', sans-serif",
                            fontStyle: 'italic',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '20px',
                            flexWrap: 'wrap'
                        }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Mouse size={16} />
                                Click and drag to navigate
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <ArrowRight size={16} />
                                Use arrow keys to move between rooms
                            </span>
                        </div>
                    </div>
                </section>

                {/* Virtual Tour Embed Section */}
                <section style={{
                    padding: '80px 20px',
                    maxWidth: '1400px',
                    margin: '0 auto'
                }}>
                    {/* Tabbed Interface for Virtual Tours */}
                    <div style={{ maxWidth: '1100px', margin: '0 auto', marginBottom: '60px' }}>
                        {/* Tabs */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '40px',
                            gap: '20px',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={() => setActiveFloor(1)}
                                style={{
                                    padding: '15px 40px',
                                    borderRadius: '50px',
                                    fontSize: '18px',
                                    fontWeight: '700',
                                    fontFamily: "'Playfair Display', serif",
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    border: activeFloor === 1 ? '2px solid #0a3f26' : '2px solid rgba(10, 63, 38, 0.1)',
                                    background: activeFloor === 1 ? '#0a3f26' : 'transparent',
                                    color: activeFloor === 1 ? '#ffffff' : '#0a3f26',
                                    boxShadow: activeFloor === 1 ? '0 10px 25px rgba(10, 63, 38, 0.2)' : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}
                            >
                                <span style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    background: activeFloor === 1 ? '#d4a575' : '#e0e0e0',
                                    color: activeFloor === 1 ? '#fff' : '#888',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '14px',
                                    fontWeight: '800'
                                }}>1</span>
                                First Floor
                            </button>

                            <button
                                onClick={() => setActiveFloor(2)}
                                style={{
                                    padding: '15px 40px',
                                    borderRadius: '50px',
                                    fontSize: '18px',
                                    fontWeight: '700',
                                    fontFamily: "'Playfair Display', serif",
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    border: activeFloor === 2 ? '2px solid #0a3f26' : '2px solid rgba(10, 63, 38, 0.1)',
                                    background: activeFloor === 2 ? '#0a3f26' : 'transparent',
                                    color: activeFloor === 2 ? '#ffffff' : '#0a3f26',
                                    boxShadow: activeFloor === 2 ? '0 10px 25px rgba(10, 63, 38, 0.2)' : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}
                            >
                                <span style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    background: activeFloor === 2 ? '#d4a575' : '#e0e0e0',
                                    color: activeFloor === 2 ? '#fff' : '#888',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '14px',
                                    fontWeight: '800'
                                }}>2</span>
                                Second Floor
                            </button>
                        </div>

                        {/* Tour Container with content transition */}
                        <div style={{
                            position: 'relative',
                            paddingBottom: 'min(56.25%, 650px)',
                            height: 0,
                            borderRadius: '20px',
                            overflow: 'hidden',
                            boxShadow: '0 30px 80px rgba(10, 63, 38, 0.2)',
                            background: '#000',
                            border: '4px solid #fff',
                            outline: '1px solid rgba(212, 165, 117, 0.3)'
                        }}>
                            {/* First Floor Iframe */}
                            <iframe
                                src="https://my.matterport.com/show/?m=zPSmG59XdRx&play=1&qs=1&gt=0&hr=0"
                                frameBorder="0"
                                allowFullScreen
                                allow="autoplay; fullscreen; web-share; xr-spatial-tracking;"
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                    opacity: activeFloor === 1 ? 1 : 0,
                                    zIndex: activeFloor === 1 ? 10 : 0,
                                    transition: 'opacity 0.5s ease-in-out',
                                    pointerEvents: activeFloor === 1 ? 'all' : 'none'
                                }}
                                title="Hollywood Royale Garden Virtual Tour - First Floor"
                            />

                            {/* Second Floor Iframe */}
                            <iframe
                                src="https://my.matterport.com/show/?m=FZKZwfBMe81"
                                frameBorder="0"
                                allowFullScreen
                                allow="autoplay; fullscreen; web-share; xr-spatial-tracking;"
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                    opacity: activeFloor === 2 ? 1 : 0,
                                    zIndex: activeFloor === 2 ? 10 : 0,
                                    transition: 'opacity 0.5s ease-in-out',
                                    pointerEvents: activeFloor === 2 ? 'all' : 'none'
                                }}
                                title="Hollywood Royale Garden Virtual Tour - Second Floor"
                            />
                        </div>

                        <div style={{
                            textAlign: 'center',
                            marginTop: '20px',
                            color: '#666',
                            fontFamily: "'Inter', sans-serif",
                            fontSize: '14px',
                            fontStyle: 'italic'
                        }}>
                            Currently Viewing: <strong style={{ color: '#0a3f26' }}>{activeFloor === 1 ? 'First Floor' : 'Second Floor'}</strong>
                        </div>
                    </div>

                    {/* Features Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: '30px',
                        marginTop: '80px'
                    }}>
                        <div style={{
                            textAlign: 'center',
                            padding: '40px 30px',
                            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                            borderRadius: '16px',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            cursor: 'default',
                            border: '2px solid rgba(212, 165, 117, 0.2)',
                            boxShadow: '0 4px 20px rgba(10, 63, 38, 0.08)'
                        }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-8px)';
                                e.currentTarget.style.boxShadow = '0 12px 40px rgba(10, 63, 38, 0.15)';
                                e.currentTarget.style.borderColor = '#d4a575';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 20px rgba(10, 63, 38, 0.08)';
                                e.currentTarget.style.borderColor = 'rgba(212, 165, 117, 0.2)';
                            }}
                        >
                            <div style={{
                                width: '70px',
                                height: '70px',
                                background: 'linear-gradient(135deg, #d4a575, #b08455)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px',
                                boxShadow: '0 8px 25px rgba(212, 165, 117, 0.4)'
                            }}>
                                <Building2 size={36} color="white" strokeWidth={2} />
                            </div>
                            <h3 style={{
                                fontSize: '22px',
                                fontWeight: '700',
                                marginBottom: '12px',
                                color: '#0a3f26',
                                fontFamily: "'Playfair Display', serif"
                            }}>
                                24/7 Professional Care
                            </h3>
                            <p style={{
                                fontSize: '15px',
                                color: '#555',
                                lineHeight: '1.7',
                                fontFamily: "'Inter', sans-serif"
                            }}>
                                Experience personalized care planning and dedicated staff who provide compassionate support around the clock, treating every resident like family
                            </p>
                        </div>

                        <div style={{
                            textAlign: 'center',
                            padding: '40px 30px',
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8fdf9 100%)',
                            borderRadius: '16px',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            cursor: 'default',
                            border: '2px solid rgba(10, 63, 38, 0.2)',
                            boxShadow: '0 4px 20px rgba(10, 63, 38, 0.08)'
                        }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-8px)';
                                e.currentTarget.style.boxShadow = '0 12px 40px rgba(10, 63, 38, 0.15)';
                                e.currentTarget.style.borderColor = '#0a3f26';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 20px rgba(10, 63, 38, 0.08)';
                                e.currentTarget.style.borderColor = 'rgba(10, 63, 38, 0.2)';
                            }}
                        >
                            <div style={{
                                width: '70px',
                                height: '70px',
                                background: 'linear-gradient(135deg, #0a3f26, #0d5232)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px',
                                boxShadow: '0 8px 25px rgba(10, 63, 38, 0.3)'
                            }}>
                                <Bed size={36} color="white" strokeWidth={2} />
                            </div>
                            <h3 style={{
                                fontSize: '22px',
                                fontWeight: '700',
                                marginBottom: '12px',
                                color: '#0a3f26',
                                fontFamily: "'Playfair Display', serif"
                            }}>
                                Private & Shared Rooms
                            </h3>
                            <p style={{
                                fontSize: '15px',
                                color: '#555',
                                lineHeight: '1.7',
                                fontFamily: "'Inter', sans-serif"
                            }}>
                                Discover bright, comfortable living spaces filled with natural light, including lovely patios and balconies with beautiful views
                            </p>
                        </div>

                        <div style={{
                            textAlign: 'center',
                            padding: '40px 30px',
                            background: 'linear-gradient(135deg, #fefefe 0%, #faf8f5 100%)',
                            borderRadius: '16px',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            cursor: 'default',
                            border: '2px solid rgba(212, 165, 117, 0.2)',
                            boxShadow: '0 4px 20px rgba(10, 63, 38, 0.08)'
                        }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-8px)';
                                e.currentTarget.style.boxShadow = '0 12px 40px rgba(10, 63, 38, 0.15)';
                                e.currentTarget.style.borderColor = '#d4a575';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 20px rgba(10, 63, 38, 0.08)';
                                e.currentTarget.style.borderColor = 'rgba(212, 165, 117, 0.2)';
                            }}
                        >
                            <div style={{
                                width: '70px',
                                height: '70px',
                                background: 'linear-gradient(135deg, #d4a575, #b08455, #0a3f26)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px',
                                boxShadow: '0 8px 25px rgba(212, 165, 117, 0.4)'
                            }}>
                                <Trees size={36} color="white" strokeWidth={2} />
                            </div>
                            <h3 style={{
                                fontSize: '22px',
                                fontWeight: '700',
                                marginBottom: '12px',
                                color: '#0a3f26',
                                fontFamily: "'Playfair Display', serif"
                            }}>
                                Beautiful Outdoor Spaces
                            </h3>
                            <p style={{
                                fontSize: '15px',
                                color: '#555',
                                lineHeight: '1.7',
                                fontFamily: "'Inter', sans-serif"
                            }}>
                                Enjoy our serene gardens, lovely patios, and nature-filled surroundings in the vibrant Hollywood neighborhood with beautiful California weather
                            </p>
                        </div>
                    </div>
                </section>

                {/* Call to Action */}
                <section style={{
                    padding: '80px 20px',
                    background: 'linear-gradient(135deg, #0a3f26 0%, #0d5232 100%)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Decorative shimmer effect */}
                    <div style={{
                        position: 'absolute',
                        top: '-50%',
                        right: '-50%',
                        width: '200%',
                        height: '200%',
                        background: 'radial-gradient(circle, rgba(212, 165, 117, 0.1) 0%, transparent 70%)',
                        pointerEvents: 'none',
                        animation: 'shimmer 8s infinite'
                    }}></div>

                    <div style={{
                        maxWidth: '900px',
                        margin: '0 auto',
                        textAlign: 'center',
                        position: 'relative',
                        zIndex: 1
                    }}>
                        <h2 style={{
                            fontSize: 'clamp(32px, 5vw, 48px)',
                            fontWeight: '800',
                            marginBottom: '20px',
                            color: '#ffffff',
                            fontFamily: "'Playfair Display', serif",
                            lineHeight: '1.2'
                        }}>
                            Ready to Visit in Person?
                        </h2>
                        <p style={{
                            fontSize: 'clamp(16px, 2vw, 20px)',
                            marginBottom: '35px',
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontFamily: "'Inter', sans-serif",
                            lineHeight: '1.6'
                        }}>
                            Schedule a tour and discover why families trust us with their loved ones. Experience the Hollywood Royale Garden difference firsthand.
                        </p>
                        <a
                            href="/contact"
                            style={{
                                display: 'inline-block',
                                padding: '16px 45px',
                                background: 'linear-gradient(135deg, #d4a575, #b08455)',
                                color: 'white',
                                textDecoration: 'none',
                                borderRadius: '50px',
                                fontWeight: '700',
                                fontSize: '16px',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: '0 8px 25px rgba(212, 165, 117, 0.4)',
                                fontFamily: "'Inter', sans-serif"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-3px)';
                                e.currentTarget.style.boxShadow = '0 12px 35px rgba(212, 165, 117, 0.5)';
                                e.currentTarget.style.background = 'linear-gradient(135deg, #b08455, #d4a575)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 8px 25px rgba(212, 165, 117, 0.4)';
                                e.currentTarget.style.background = 'linear-gradient(135deg, #d4a575, #b08455)';
                            }}
                        >
                            Schedule a Tour Today
                        </a>
                    </div>
                </section>
            </main>

            <Footer />

            <style>{`
                @keyframes shimmer {
                    0%, 100% {
                        transform: translate(0, 0);
                    }
                    50% {
                        transform: translate(-30%, -30%);
                    }
                }
            `}</style>
        </SmoothScroll>
    );
};

export default VirtualTourPage;
